/* eslint-disable react/require-default-props */
import React from 'react';
import { Dropdown, OverlayTrigger, Popover } from 'react-bootstrap';
import 'react-toggle/style.css';

import Button from '../Button/Button';
import Toggle from '../Toggle/Toggle';
import './PlayerControlSettings.scss';

export interface PlayerControlSettingsProps {
  playbackRate?: number;
  unlocked?: boolean;
  autoAdvance?: boolean;
  showCaptions?: boolean;
  updatePlaybackRate: (value: number) => void;
  updateAutoAdvance: (value: boolean) => void;
  updateShowCaptions: () => void;
}

const PlayerControlSettings = React.forwardRef<HTMLDivElement, PlayerControlSettingsProps>(
  (
    {
      playbackRate = 1,
      unlocked = false,
      autoAdvance = false,
      showCaptions = false,
      updatePlaybackRate,
      updateAutoAdvance,
      updateShowCaptions,
      ...props
    },
    ref,
  ) => {
    const playbackRates = [0.5, 0.75, 1, 1.25, 1.5, 1.75, 2];
    return (
      <div {...props} ref={ref} className="player-control-settings">
        <div className="dropdown-controls">
          <Dropdown drop="right">
            <Dropdown.Toggle className="settings-dropdown-toggle">
              <div className="dropdown-button">
                <span>Captions (auto-generated)</span>
                <span className="material-icons">chevron_right</span>
              </div>
            </Dropdown.Toggle>
            <Dropdown.Menu>
              <Dropdown.Item
                className="settings-option"
                eventKey="on"
                active={showCaptions}
                onClick={() => updateShowCaptions()}
              >
                On (English)
              </Dropdown.Item>
              <Dropdown.Item
                className="settings-option"
                eventKey="off"
                active={!showCaptions}
                onClick={() => updateShowCaptions()}
              >
                Off
              </Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>
        </div>
        <div className="dropdown-controls">
          <Dropdown drop="right">
            <Dropdown.Toggle className="settings-dropdown-toggle">
              <div className="dropdown-button">
                <span>Speed</span>
                <span className="material-icons">chevron_right</span>
              </div>
            </Dropdown.Toggle>
            <Dropdown.Menu>
              {playbackRates.map((rate) => {
                const menuProps = {
                  key: '',
                  eventKey: `${rate}`,
                  active: playbackRate === rate,
                  disabled: false,
                };

                if (rate > 1 && !unlocked) {
                  const tooltip = (
                    <Popover id={`${rate}`} className="player-control-settings__Tooltip">
                      <Popover.Content>
                        Speeding up the timeline is disabled until you have completed this activity.
                      </Popover.Content>
                    </Popover>
                  );

                  menuProps.disabled = !unlocked;
                  return (
                    <OverlayTrigger key={`rate${rate}`} overlay={tooltip}>
                      <Dropdown.Item className="disabled-rate-option settings-option">
                        {rate}x
                      </Dropdown.Item>
                    </OverlayTrigger>
                  );
                }

                menuProps.key = `rate${rate}`;
                return (
                  <Dropdown.Item
                    className="settings-option"
                    {...menuProps}
                    onClick={() => updatePlaybackRate(rate)}
                  >
                    {rate}x
                  </Dropdown.Item>
                );
              })}
            </Dropdown.Menu>
          </Dropdown>
        </div>
        <div className="auto-advance">
          <div className="auto-advance-text">
            <div>Autoadvance</div>
            <Button
              tooltipPlacement="top"
              tooltip="Advance automatically to the next screen when the timeline completes."
              unstyledIcon
              className="info-icon"
              noStyling
            >
              info_outline
            </Button>
          </div>
          <Toggle checked={autoAdvance || false} onChange={updateAutoAdvance} />
        </div>
      </div>
    );
  },
);

export default PlayerControlSettings;
